import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, CircularProgress, Typography, Paper, Button, Container } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';

interface WithRoleProps {
  allowedRoles: string[];
  children: React.ReactNode;
}

const WithRole: React.FC<WithRoleProps> = ({ allowedRoles, children }) => {
  const { userRole, loading } = useAuth();
  const location = useLocation();
  
  // Show a better loading indicator with a timeout
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Authentifizierung wird überprüft...
        </Typography>
      </Box>
    );
  }

  // If user is not authenticated, redirect to login
  if (!userRole) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  
  // If user doesn't have the required role, show access denied
  if (!allowedRoles.includes(userRole)) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Zugriff verweigert
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Du hast nicht die erforderlichen Berechtigungen, um auf diese Seite zuzugreifen. 
            Für den Zugriff ist eine der folgenden Rollen erforderlich: {allowedRoles.join(', ')}.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Deine aktuelle Rolle: <strong>{userRole}</strong>
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => window.history.back()}
          >
            Zurück
          </Button>
        </Paper>
      </Container>
    );
  }

  // User is authenticated and has the required role
  return <>{children}</>;
};

export default WithRole;